import React, { useEffect } from 'react';

// Components
import Tab, { ITabProps } from './Tab';
import { BaseStyleProps, TabsWrapper } from './Tabs.style';
import Panel, { IPanelProps } from './Panel';

// Utils.
import { useDidUpdateEffect } from '../../../utils/hooks';
import { TabsContext } from './createContextTab';


/**
 * Composition of the Tabs compound component.
 */
export interface ITabsComposition {
  Tab: React.FC<ITabProps>;
  Panel: React.FC<IPanelProps>;
}

export interface ITabsProps extends BaseStyleProps {
  /**
   * Label of the Tab to have open on first render.
   * If activeTab is set, this prop won't have any effect.
   * If both activeTab and defaultActiveTab are missing,
   * there will be no active tab on the first render.
   */
  defaultActiveTab?: string;

  /**
   * Id of the active tab.
   * If set, the defaultActiveTab won't have any effect
   * If both activeTab and defaultActiveTab are missing,
   * there will be no active tab on the first render.
   */
  activeTab?: string;

  /**
   * Whether the Tabs component should stretch the full width of its container
   */
  fullWidth?: boolean;

  /**
   * Fired when an active Tab is changed.
   */
  onChange?: (tabLabel: string) => void;

  /**
   * Fired when a tab is clicked.
   */
  onClick?: (tabLabel: string) => void;

  children?: React.ReactNode;
  
  id?: string;
}

/**
 * Tab container.
 */
const Tabs: React.FC<ITabsProps> & ITabsComposition = ({
  defaultActiveTab,
  fullWidth = false,
  onChange,
  onClick,
  activeTab: activeTabProp,
  children,
  id,
  ...rest
}) => {
  const [activeTab, setActiveTab] = React.useState(
    activeTabProp || defaultActiveTab || ''
  );

  useEffect(() => {
    if (activeTabProp) setActiveTab(activeTabProp);
  }, [activeTabProp]);

  const handleTabClick = (tabLabel: string): void => {
    if (!activeTabProp) setActiveTab(tabLabel);
    if (onClick) onClick(tabLabel);
  };

  const contextValue = {
    id,
    activeTab,
    fullWidth,
    onClick: handleTabClick
  };

  /**
   * Fire change handler when the active Tab changes.
   */
  useDidUpdateEffect(() => {
    if (onChange) {
      onChange(activeTab);
    }
  }, [activeTab]);

  return (
    <TabsContext.Provider value={contextValue}>
      <TabsWrapper role="tablist" {...rest}>
        {children}
      </TabsWrapper>
    </TabsContext.Provider>
  );
};

/**
 * Hook which exposes parts of the `ITabsContext` for state
 * consumption.
 */
// export const useTabs = (): ITabsContext => {
//   const context = useContext(TabsContext);
//   if (!context) {
//     throw new Error('This component must be used within a <Tabs> component.');
//   }
//   return context;
// };

// Composed components.
Tabs.Tab = Tab;
Tabs.Panel = Panel;

// Display names.
Tabs.Tab.displayName = 'Tabs.Tab';
Tabs.Panel.displayName = 'Tabs.Panel';

export default Tabs;
